import { createTheme, responsiveFontSizes } from '@mui/material/styles';

// The blog (frontend/blog) mirrors these colours, see its README "Theme contract"
const palettes = {
  light: {
    mode: 'light',
    background: {
      default: '#FFF8F0', // Cream, same warm background as the live quiz theme
      paper: '#ffffff',
    },
    primary: {
      main: '#000000', // Black primary for strong contrast
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#3d5afe', // Vibrant Blue for accents
      contrastText: '#ffffff',
    },
    text: {
      primary: '#1a1a1a', // Nearly black
      secondary: '#666666',
    },
    divider: '#eaeaea',
  },
  dark: {
    mode: 'dark',
    background: {
      default: '#121212',
      paper: '#1c1c1e',
    },
    primary: {
      main: '#ffffff', // Inverted brand: white primary on dark backgrounds
      contrastText: '#000000',
    },
    secondary: {
      main: '#7B90FF', // Lightened blue for adequate contrast on dark backgrounds
      contrastText: '#000000',
    },
    text: {
      primary: '#f0f0f0',
      secondary: '#a3a3a3',
    },
    divider: 'rgba(255,255,255,0.12)',
  },
};

// getLiveTheme() also calls getTheme() as its base on every toggle; this
// cache keeps that a lookup instead of a second full theme rebuild.
const themeCache = {};

export default function getTheme(mode) {
  if (themeCache[mode]) return themeCache[mode];

  const palette = palettes[mode] || palettes.light;

  let theme = createTheme({
    palette,
    typography: {
      fontFamily: "'Inter', 'Roboto', 'Helvetica', 'Arial', sans-serif",
      h1: {
        fontWeight: 900,
        letterSpacing: '-0.03em',
        fontSize: '3.5rem',
        color: palette.text.primary,
      },
      h2: {
        fontWeight: 800,
        letterSpacing: '-0.02em',
      },
      h3: {
        fontWeight: 700,
        letterSpacing: '-0.01em',
      },
      h6: {
        fontWeight: 600,
        fontFamily: "'Inter', sans-serif",
        textTransform: 'uppercase',
        letterSpacing: '0.1em',
        fontSize: '0.875rem',
        color: palette.text.secondary,
      },
      body1: {
        lineHeight: 1.8,
        fontSize: '1.05rem',
        color: palette.text.primary,
      },
      button: {
        fontWeight: 600,
        textTransform: 'none',
        borderRadius: 0, // Sharp edges for modern brutalist/minimalist feel
      },
    },
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: 4, // Slight rounding
            padding: '10px 24px',
            boxShadow: 'none',
            '&:hover': {
              boxShadow: 'none',
              backgroundColor: palette.primary.main,
              color: palette.primary.contrastText,
            },
          },
          outlined: {
            borderWidth: '1px',
            borderColor: palette.primary.main,
            color: palette.primary.main,
            '&:hover': {
              borderWidth: '1px',
              backgroundColor: palette.primary.main,
              color: palette.primary.contrastText,
            },
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            boxShadow: 'none',
            border: `1px solid ${palette.divider}`,
          },
          rounded: {
            borderRadius: 8,
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          // Scoped to default-coloured chips: as a flat `root` rule this grey also
          // won over filledSuccess and friends, so a semantic chip came out grey
          // with white-on-grey text. Tag chips look exactly as before.
          // Needs `theme` (unlike the overrides above) for palette.grey, which
          // is MUI's computed scale, not one of our own palette values.
          root: ({ ownerState, theme }) => ({
            fontWeight: 500,
            borderRadius: 4,
            ...(ownerState.color === 'default' && {
              backgroundColor: theme.palette.grey[mode === 'light' ? 200 : 800],
            }),
          }),
        },
      },
    },
  });

  theme = responsiveFontSizes(theme);
  themeCache[mode] = theme;
  return theme;
}
